import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "../../components/Buttons/Button";
import LinearLoaderWithTime from "../../components/Loaders/LinearLoaderWithTime";
import {
  APPSUMO_CALLBACK_CONNECTING,
  APPSUMO_CALLBACK_ERROR_TITLE,
  APPSUMO_CALLBACK_ERROR_DESCRIPTION,
  APPSUMO_CALLBACK_ERROR_OAUTH_DENIED,
  APPSUMO_CALLBACK_BUTTON_RETURN_TO_APPSUMO,
  APPSUMO_CALLBACK_ERROR_ANOMALOUS,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import { startLogout } from "../../actions/auth";
import { startAppSumoCallback } from "../../actions/user";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../../lib/firebase";

import { LaunchError } from "../../utils/errors";

const APPSUMO_MARKETPLACE_URL = "https://appsumo.com/account/products";
const APPSUMO_LICENSE_KEY = "appSumoLicenseKey";

const AppSumoCallback = () => {
  const [status, setStatus] = useState("loading");
  const [errorMessageKey, setErrorMessageKey] = useState(
    APPSUMO_CALLBACK_ERROR_DESCRIPTION
  );

  // Prevent double-execution in React Strict Mode (code is single-use)
  const codeConsumedRef = useRef(false);

  const [params] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatchApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const { t } = useTranslationApp();

  const code = params.get("code");
  const error = params.get("error");

  const onHandleCallback = async () => {
    if (codeConsumedRef.current) return;
    codeConsumedRef.current = true;

    try {
      // Do not associate AppSumo under an existing account: ensure a clean session first.
      if (auth.currentUser) {
        await dispatch(startLogout());
      }

      const response = await dispatch(startAppSumoCallback({ code }));

      // Clean the authorization code from the URL to avoid accidental reuse
      navigate("/appSumo/callback", { replace: true });

      if (!response.ok) {
        setErrorMessageKey(APPSUMO_CALLBACK_ERROR_DESCRIPTION);
        setStatus("error");
        return;
      }

      const { isRegistered, customToken, licenseKey } = response.data || {};

      if (isRegistered && customToken) {
        await signInWithCustomToken(auth, customToken);
        navigate("/", { replace: true });
        return;
      }

      if (!isRegistered && licenseKey) {
        sessionStorage.setItem(APPSUMO_LICENSE_KEY, licenseKey);
        navigate("/appSumo/register", { replace: true });
        return;
      }

      // isRegistered === true but no customToken: anomalous case from backend
      console.error(
        "[AppSumoCallback] Anomalous response: isRegistered=true but no customToken.",
        { isRegistered, hasToken: Boolean(customToken) }
      );
      setErrorMessageKey(APPSUMO_CALLBACK_ERROR_ANOMALOUS);
      setStatus("error");
    } catch (err) {
      dispatch(
        LaunchError(err, false, [], {
          functionName: "onHandleCallback",
          capture: "exception",
        })
      );
      console.error("[AppSumoCallback] Unexpected error", {
        code: err?.code || null,
        name: err?.name || null,
      });
      setErrorMessageKey(APPSUMO_CALLBACK_ERROR_DESCRIPTION);
      setStatus("error");
    }
  };

  useEffect(() => {
    if (error) {
      setErrorMessageKey(APPSUMO_CALLBACK_ERROR_OAUTH_DENIED);
      setStatus("error");
      return;
    }

    if (!code) {
      navigate("/login", { replace: true });
      return;
    }

    onHandleCallback();
    // eslint-disable-next-line
  }, []);

  const logoStyles = {
    width: "auto",
    height: "auto",
    maxHeight: "60px",
    ".MuiAvatar-img": {
      maxWidth: "160px",
      objectFit: "contain",
    },
    mb: 3,
  };

  if (status === "error") {
    return (
      <Container
        maxWidth="xs"
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          textAlign: "center",
        }}
      >
        <Avatar
          src={theme.logoLogin}
          alt="Logo"
          variant="square"
          sx={logoStyles}
        />
        <Typography variant="h6" fontWeight={700} mb={1}>
          {t(APPSUMO_CALLBACK_ERROR_TITLE)}
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {t(errorMessageKey)}
        </Typography>
        <Box
          component="a"
          href={APPSUMO_MARKETPLACE_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textDecoration: "none", width: "100%" }}
        >
          <Button variant="contained" color="primary" fullWidth>
            {t(APPSUMO_CALLBACK_BUTTON_RETURN_TO_APPSUMO)}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="xs"
      sx={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Avatar
        src={theme.logoLogin}
        alt="Logo"
        variant="square"
        sx={logoStyles}
      />
      <LinearLoaderWithTime
        sx={{ mt: 2 }}
        label={t(APPSUMO_CALLBACK_CONNECTING)}
        time={30 * 1000}
      />
    </Container>
  );
};

export default AppSumoCallback;
